/**
 * Production adapter for `set_window_level`: the SAME steps as the
 * viewport overlay's image controls —
 *  - preset dropdown (`WindowingPresetMenu`): `buildPresetOptions` for the
 *    loaded image, then `WindowLevelSetEvent` with the preset's width and
 *    centre;
 *  - blend slider (`ImageBlendSlider`): `updatePrimaryImageAlpha(percent)`,
 *    which also stores the slider value;
 *  - fusion colour selector (`FusionColorSelector`):
 *    `updateSelectedFusionColorMapping` with the exported Grayscale or
 *    Cyan–Magenta mapping.
 * Which fusion controls apply is read from the same atoms `ImageOptions`
 * uses (second image, PET on either side, blend mode). PET is detected from
 * the modality directly rather than via `usePetImage`, whose SUV sync
 * effect would run a second time. Browser glue covered by the manual demo;
 * the logic is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { Modality } from '~/domain/dicom/modality';
import { domainEventBus, WindowLevelSetEvent } from '~/domain/events';
import type { Image3D } from '~/domain/patient/image3d';
import { buildPresetOptions, WindowLevel } from '~/domain/patient/windowLevel';
import { ImageViewMode } from '~/engine/core/render/renderers/ImageRenderer';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    cyanMagentaFusionColorMapping,
    grayscaleFusionColorMapping,
    primaryImageState,
    secondaryImageState,
    selectedSecondaryImageModeState,
} from '~/state/atoms/image/imageAtoms';
import type { FusionDisplay, WindowLevelDeps } from './windowLevel';

const isPet = (image: Image3D | undefined): boolean =>
    image?.firstSlice.modality === Modality.PT;

export function useWindowLevelDeps(): () => WindowLevelDeps {
    const env = useRecoilValue(environmentState);
    const primaryImage = useRecoilValue(primaryImageState);
    const secondaryImage = useRecoilValue(secondaryImageState);
    const viewMode = useRecoilValue(selectedSecondaryImageModeState);
    const { updatePrimaryImageAlpha, updateSelectedFusionColorMapping } =
        useViewportEnvironment();

    return useCallback((): WindowLevelDeps => {
        const image = env?.image?.parent;
        const fusionDisplay = (): FusionDisplay => {
            if (!secondaryImage) {
                return 'single-image';
            }
            if (isPet(secondaryImage) || isPet(primaryImage)) {
                return 'pet-fusion';
            }
            return viewMode === ImageViewMode.AlphaBlending
                ? 'alpha-blending'
                : 'checkerboard';
        };
        return {
            presets: () => (image ? buildPresetOptions(image) : undefined),
            apply: (preset) => {
                if (!image) {
                    return;
                }
                domainEventBus.publishSync(
                    new WindowLevelSetEvent(
                        new WindowLevel(preset.ww, preset.wc),
                        image.seriesUid,
                        'user'
                    )
                );
            },
            fusionDisplay,
            setBlend: (percent) => updatePrimaryImageAlpha(percent),
            setFusionColors: (colors) =>
                updateSelectedFusionColorMapping(
                    colors === 'grayscale'
                        ? grayscaleFusionColorMapping
                        : cyanMagentaFusionColorMapping
                ),
        };
    }, [
        env,
        primaryImage,
        secondaryImage,
        viewMode,
        updatePrimaryImageAlpha,
        updateSelectedFusionColorMapping,
    ]);
}
